import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, useEffect } from 'react';

import { maskStyle } from 'utils';
import { CourseStepStatus } from 'types';
import { getStepData } from 'utils/steps';
import { useCourseContext } from 'context';

import { StepButtonProps } from './types';

export const StepButton = ({ item, title, onClick }: StepButtonProps) => {
  const [isHovered, setHovered] = useState(false);
  const { isFinished, getStepStatus } = useCourseContext();
  const { t } = useTranslation(['dashboard']);

  const { id, color, coverImage, resourceVersions } = item;
  const { primaryClassName, secondaryClassName } = useMemo(
    () => getStepData(color),
    [color]
  );

  const status = useMemo(() => getStepStatus(id), [getStepStatus, id]);

  const isResource = resourceVersions && resourceVersions.length > 0;

  const isActive = status === CourseStepStatus.Active;
  const isLocked = status === CourseStepStatus.Locked;
  const isCompleted = status === CourseStepStatus.Completed;
  const isColored = isActive || isFinished;

  const isClickable = isActive || (isResource && isCompleted);

  useEffect(() => {
    if (!isClickable && isHovered) {
      setHovered(false);
    }
  }, [isClickable, isHovered]);

  const handleClick = isClickable ? onClick : undefined;
  const handleMouseEnter = isClickable ? () => setHovered(true) : undefined;
  const handleMouseLeave = isClickable ? () => setHovered(false) : undefined;

  const iconUrl = isLocked ? '/images/lock-on.svg' : coverImage;
  const iconLabel = isLocked ? `${title}, ${t('statusLocked')}` : title;

  return (
    <div
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={twMerge(
        'relative flex h-6 w-6 items-center justify-center rounded-full md:h-9 md:w-9',
        isLocked && 'opacity-40',
        isClickable &&
          'hover:bg-accent-gradient-secondary-hover active:bg-accent-gradient-secondary-active cursor-pointer',
        isColored ? primaryClassName : secondaryClassName,
        isActive &&
          'hover:bg-accent-gradient-primary-hover active:bg-accent-gradient-primary-active'
      )}
    >
      <div
        role="img"
        aria-label={iconLabel}
        style={maskStyle(iconUrl)}
        className={twMerge(
          isColored ? 'bg-white-primary' : primaryClassName,
          isLocked ? 'h-3 w-3 md:h-5 md:w-5' : 'h-4 w-4 md:h-6 md:w-6'
        )}
      />
    </div>
  );
};
